/**
 * WebSocket transport.
 *
 * Owns exactly one socket and the reconnect policy around it. The server has no
 * guarantee of staying up and mobile clients suspend aggressively, so the
 * contract here is: a `Connection` is always trying to be connected, and
 * callers may send at any time without checking readiness.
 *
 * Everything above this layer sees a stream of `ServerFrame`s and a status.
 */

import {
  CLOSE_LOGGED_OUT,
  isServerFrame,
  type ClientFrame,
  type ServerFrame,
} from "@/chat/protocol"

export type ConnectionStatus = "connecting" | "online" | "offline"

type Listener = {
  onFrame: (frame: ServerFrame) => void
  onStatus: (status: ConnectionStatus) => void
  /**
   * The server closed the socket because the session token was revoked
   * (`/api/auth/logout`, possibly from another device). No reconnect follows:
   * the token is dead and only a fresh sign-in can produce a live one.
   */
  onLoggedOut: () => void
}

/** Reconnect backoff: 500ms doubling to 15s, with jitter to avoid a thundering herd. */
const BASE_DELAY_MS = 500
const MAX_DELAY_MS = 15_000

export function resolveSocketUrl(): string {
  const configured = import.meta.env.VITE_WS_URL
  if (configured) return configured

  const scheme = location.protocol === "https:" ? "wss" : "ws"
  return `${scheme}://${location.host}/ws`
}

export class Connection {
  private socket: WebSocket | null = null
  private listener: Listener | null = null
  private retries = 0
  private timer: ReturnType<typeof setTimeout> | null = null
  private disposed = false

  /**
   * Frames sent before the socket is open, replayed on connect. This is what
   * lets callers send without caring about readiness — notably the `hello`
   * handshake, which is queued once and re-sent by `onOpen` on every reconnect.
   */
  private queue: ClientFrame[] = []

  /**
   * Rebuilt by the owner on every (re)connect: the frames needed to restore
   * session state on a fresh socket. Kept separate from `queue` so it survives
   * being flushed.
   */
  handshake: (() => ClientFrame[]) | null = null

  private status: ConnectionStatus = "connecting"

  // Written out rather than declared as a constructor parameter property, which
  // `erasableSyntaxOnly` disallows.
  private readonly url: string

  constructor(url: string) {
    this.url = url
  }

  listen(listener: Listener): void {
    this.listener = listener
    listener.onStatus(this.status)
  }

  start(): void {
    if (this.disposed || this.socket) return
    this.open()
  }

  private setStatus(status: ConnectionStatus): void {
    if (this.status === status) return
    this.status = status
    this.listener?.onStatus(status)
  }

  private open(): void {
    this.setStatus(this.retries === 0 ? "connecting" : "offline")

    let socket: WebSocket
    try {
      socket = new WebSocket(this.url)
    } catch {
      // A malformed URL throws synchronously; treat it as a failed attempt so
      // the backoff still applies rather than spinning.
      this.scheduleReconnect()
      return
    }

    this.socket = socket

    socket.onopen = () => {
      // React StrictMode mounts, unmounts and remounts effects in development,
      // so a socket can finish connecting after its owner is already gone.
      if (this.disposed || this.socket !== socket) {
        socket.close(1000, "superseded")
        return
      }

      this.retries = 0
      this.setStatus("online")

      for (const frame of this.handshake?.() ?? []) {
        this.write(frame)
      }

      const queued = this.queue
      this.queue = []
      for (const frame of queued) this.write(frame)
    }

    socket.onmessage = (event) => {
      let parsed: unknown
      try {
        parsed = JSON.parse(String(event.data))
      } catch {
        return // A frame we cannot parse is a frame we cannot act on.
      }

      if (!isServerFrame(parsed)) return

      // The heartbeat is transport-level; answer it without waking the store.
      if (parsed.type === "ping") {
        this.write({ type: "pong" })
        return
      }

      this.listener?.onFrame(parsed)
    }

    socket.onerror = () => {
      // `onclose` always follows, which is where reconnection is handled.
    }

    socket.onclose = (event) => {
      this.socket = null
      if (this.disposed) return

      if (event.code === CLOSE_LOGGED_OUT) {
        // Reconnecting would only re-send a revoked token and get
        // `unauthorized` back; stop here and let the owner sign out.
        this.disposed = true
        this.setStatus("offline")
        this.listener?.onLoggedOut()
        return
      }

      this.setStatus("offline")
      this.scheduleReconnect()
    }
  }

  private scheduleReconnect(): void {
    if (this.disposed || this.timer) return

    const backoff = Math.min(BASE_DELAY_MS * 2 ** this.retries, MAX_DELAY_MS)
    this.retries += 1

    // Jitter spreads reconnects when a server restart drops every client at once.
    const delay = backoff * (0.5 + Math.random() * 0.5)

    this.timer = setTimeout(() => {
      this.timer = null
      this.open()
    }, delay)
  }

  private write(frame: ClientFrame): void {
    this.socket?.send(JSON.stringify(frame))
  }

  /** Sends now if the socket is open, otherwise queues until it is. */
  send(frame: ClientFrame): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.write(frame)
    } else {
      this.queue.push(frame)
    }
  }

  /**
   * Drops a queued frame that is no longer worth replaying — a `typing` notice
   * that would arrive seconds late is noise, not information.
   */
  sendEphemeral(frame: ClientFrame): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.write(frame)
  }

  /** Brings the socket back immediately, for a manual "retry" affordance. */
  reconnectNow(): void {
    if (this.disposed) return
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
    this.retries = 0
    if (this.socket) this.socket.close()
    else this.open()
  }

  dispose(): void {
    this.disposed = true
    if (this.timer) clearTimeout(this.timer)
    this.timer = null
    this.listener = null

    const socket = this.socket
    this.socket = null
    if (!socket) return

    // Calling close() on a socket that is still CONNECTING aborts the handshake
    // and logs "closed before the connection is established" in every browser.
    // Nothing is broken by it, but under StrictMode it happens on every mount,
    // so let the handshake settle and close it in `onopen` instead.
    if (socket.readyState === WebSocket.CONNECTING) return

    socket.close(1000, "disposed")
  }
}
